import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import CompanyPage from "../company/CompanyPage";
import {
  PERF_METRICS,
  buildPerfChartData,
  HorizontalPerfBarChart,
} from "./PerformanceBarChartsShared";

/**
 * Top N rows for one metric by performance (desc). buildPerfChartData already sorts best-first.
 */
export function buildTopNPerfChartData(stockReports, metricKey, n = 10) {
  const ranked = buildPerfChartData(stockReports, metricKey);
  return ranked.slice(0, n);
}

/**
 * Same horizontal perf layout as Index Tracker; each chart shows up to N holdings
 * with the strongest return for that period (among rows with data for that field).
 */
export default function ScreenerTopPerformanceCharts({ stockReports, topN = 10 }) {
  const [modalState, setModalState] = useState({ open: false, symbol: "" });

  const openSymbolProfile = useCallback((symbol) => {
    if (!symbol) return;
    setModalState({ open: true, symbol });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((s) => ({ ...s, open: false }));
  }, []);

  const chartsByMetric = useMemo(() => {
    const out = {};
    for (const m of PERF_METRICS) {
      out[m.key] = buildTopNPerfChartData(stockReports, m.key, topN);
    }
    return out;
  }, [stockReports, topN]);

  if (!stockReports?.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        No holdings data.
      </Typography>
    );
  }

  const hasAnyChart = PERF_METRICS.some((m) => (chartsByMetric[m.key] || []).length > 0);

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2 },
        pb: 4,
        maxWidth: { xs: "100%", md: 1320 },
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ mt: 0.5, mb: 0.5, fontWeight: 700, width: "100%", textAlign: "center" }}
      >
        Top {topN} stocks by performance — per period
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1.5, display: "block", width: "100%", textAlign: "center" }}
      >
        Each section ranks holdings by that
        horizon&apos;s return (highest first). Names can differ from chart to chart.
      </Typography>

      {!hasAnyChart && (
        <Typography color="text.secondary" sx={{ mb: 2, textAlign: "center", width: "100%" }}>
          No performance values found on these holdings for any period.
        </Typography>
      )}

      <Grid container spacing={2} sx={{ width: "100%", alignItems: "stretch" }}>
        {PERF_METRICS.map((m) => {
          const data = chartsByMetric[m.key] || [];
          return (
            <Grid item xs={12} sm={6} key={m.key}>
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  pb: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    color: "text.primary",
                    letterSpacing: 0.02,
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  {m.label.toUpperCase()}
                </Typography>
                {!data.length ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block", textAlign: "center", flex: 1 }}
                  >
                    No data for this period.
                  </Typography>
                ) : (
                  <HorizontalPerfBarChart
                    data={data}
                    metricLabel={m.label}
                    chartKey={`screener-topperf-${topN}-${m.key}`}
                    onSymbolClick={openSymbolProfile}
                    fillContainer
                  />
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Modal
        closeOnEscape={true}
        onClose={closeModal}
        centered
        open={modalState.open}
        size="fullscreen"
        style={{
          marginLeft: "1%",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Modal.Content scrolling style={{ padding: "0.25rem" }}>
          <CompanyPage symbol={modalState.symbol} />
        </Modal.Content>
        <Modal.Actions style={{ padding: "0.25rem" }}>
          <Button
            className="ui blue button"
            onClick={closeModal}
            style={{
              borderRadius: "4px",
              padding: "0.4rem 1rem",
              fontWeight: "500",
            }}
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </Box>
  );
}
